"use client";

import type React from "react";

import { type ReactNode, useRef, useState, useEffect } from "react";

interface AccordionProp {
  title: string;
  children: ReactNode;
  defaultState?: boolean;
  svgIcon?: "caution";
}

const AccordionBox: React.FC<AccordionProp> = ({
  title,
  children,
  defaultState = true,
  svgIcon,
}) => {
  const [activeTab, setActiveTab] = useState(defaultState);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  // Update height when content changes or when accordion opens/closes
  useEffect(() => {
    if (contentRef.current) {
      // Force a reflow to get the correct scrollHeight
      const scrollHeight = contentRef.current.scrollHeight;
      setContentHeight(activeTab ? scrollHeight : 0);
    }
  }, [activeTab, children]);

  // Add a resize observer to handle dynamic content changes
  useEffect(() => {
    if (!contentRef.current) return;

    const node = contentRef.current;

    const resizeObserver = new ResizeObserver(() => {
      if (activeTab && node) {
        setContentHeight(node.scrollHeight);
      }
    });

    resizeObserver.observe(node);

    return () => {
      if (node) {
        resizeObserver.unobserve(node);
      }
    };
  }, [activeTab]);

  return (
    <div className="overflow-hidden w-full">
      <button
        onClick={() => setActiveTab((prev) => !prev)}
        className="w-full flex justify-between items-center h-20 p-4 px-8 bg-primary transition-all ease-linear"
      >
        <div className="flex items-center space-x-[8px]">
          {svgIcon && svgOption[svgIcon]}
          <span className="text-white text-2xl font-bold">{title}</span>
        </div>
        <figure className="">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`!size-7 text-gray-600 stroke-pink-400 transition-transform duration-200 ${
              !activeTab ? "rotate-180" : ""
            }`}
          >
            <path
              d="M18 15L12 9L6 15"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </figure>
      </button>
      <div
        ref={contentRef}
        className="transition-[max-height] duration-300 ease-in-out overflow-hidden"
        style={{ maxHeight: `${contentHeight}px` }}
      >
        {children}
      </div>
    </div>
  );
};

export default AccordionBox;

const svgOption = {
  caution: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.2898 3.86118L1.81978 18.0012C1.64514 18.3036 1.55274 18.6465 1.55177 18.9957C1.55079 19.3449 1.64127 19.6883 1.8142 19.9917C1.98714 20.2951 2.3365 20.5479 2.53748 20.725C2.83847 20.9021 3.18058 20.9973 3.52978 21.0012H20.4698C20.819 20.9973 21.1611 20.9021 21.4621 20.725C21.7631 20.5479 22.0124 20.2951 22.1854 19.9917C22.3583 19.6883 22.4488 19.3449 22.4478 18.9957C22.4468 18.6465 22.3544 18.3036 22.1798 18.0012L13.7098 3.86118C13.5315 3.56729 13.2805 3.3243 12.981 3.15567C12.6814 2.98703 12.3435 2.89844 11.9998 2.89844C11.656 2.89844 11.3181 2.98703 11.0186 3.15567C10.7191 3.3243 10.468 3.56729 10.2898 3.86118Z"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 9V13"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 17H12.01"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};
